import { useMemo } from "react";
import { ListPlus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { MemberAssignedRating } from "../../types";
import { isSyntheticSource } from "../../calc";
import { DataGrid, type GridColumn } from "../DataGrid";
import { IconButton } from "../IconButton";
import { InlineNumberEdit } from "../InlineNumberEdit";
import { SourcePill, TagPill } from "../Pills";
import { CheckboxInput } from "../fields/CheckboxInput";
import { ToggleInput } from "../fields/ToggleInput";
import { EmptyState } from "../ScreenStates";
import { NoteEditor } from "./NoteEditor";
import { formatCurrency } from "../../utils/format";
import { LOCKED_TOOLTIP } from "../group/memberOrdering";
import { DEFAULT_SORT, sortByColumnThenConditionAndRating } from "./assignedRatingOrdering";

interface AssignedRatingsGridProps {
  ratings: readonly MemberAssignedRating[];
  isLocked: boolean;
  showChangeReason: boolean;
  addConditionTo: string;
  onDebitsChange: (rating: MemberAssignedRating, debits: number) => Promise<boolean>;
  onUwDollarsChange: (rating: MemberAssignedRating, dollars: number | null) => Promise<boolean>;
  onToggleExcluded: (rating: MemberAssignedRating, excluded: boolean) => void;
  onIrixSourceChange: (rating: MemberAssignedRating, irixSource: boolean) => void;
  onNotesChange: (rating: MemberAssignedRating, notes: string | null) => Promise<boolean>;
  onRemove: (rating: MemberAssignedRating) => void;
  children?: never;
}

export function AssignedRatingsGrid({ ratings, isLocked, showChangeReason, addConditionTo, onDebitsChange, onUwDollarsChange, onToggleExcluded, onIrixSourceChange, onNotesChange, onRemove }: AssignedRatingsGridProps) {
  const columns = useMemo<GridColumn<MemberAssignedRating>[]>(() => {
    const base: GridColumn<MemberAssignedRating>[] = [
      { key: "condition", header: "Condition", sortValue: (rating) => rating.conditionName, render: (rating) => <span className={isSyntheticSource(rating.source) ? "italic text-gray-600" : "font-medium text-gray-800"}>{rating.conditionName}</span> },
      { key: "rating", header: "Rating", sortValue: (rating) => rating.ratingText, render: (rating) => <span className={isSyntheticSource(rating.source) ? "italic text-gray-600" : ""}>{rating.ratingText}</span> },
      { key: "time", header: "Time", render: (rating) => rating.time ?? <span className="text-gray-400">–</span> },
      {
        key: "debits",
        header: "Debits",
        align: "right",
        sortValue: (rating) => rating.assignedDebits,
        render: (rating) => <InlineNumberEdit id={`debits-${rating.id}`} label={`debits for ${rating.ratingText}`} value={rating.assignedDebits} defaultValue={rating.defaultDebits} disabled={isLocked} onSave={(value) => (value === null ? false : onDebitsChange(rating, value))} />,
      },
      {
        key: "uwDollars",
        header: "UW Dollars",
        align: "right",
        render: (rating) => <InlineNumberEdit id={`dollars-${rating.id}`} label={`UW Dollars for ${rating.ratingText}`} value={rating.uwDollars} disabled={isLocked} allowEmpty format={(value) => (value === null ? "" : formatCurrency(value))} onSave={(value) => onUwDollarsChange(rating, value)} />,
      },
      { key: "notes", header: "Notes", render: (rating) => <NoteEditor commit="save" preview="expandable" notes={rating.notes} label={`notes for ${rating.ratingText}`} disabled={isLocked} onSave={(notes) => onNotesChange(rating, notes)} /> },
      { key: "excluded", header: "Excluded", align: "center", render: (rating) => <ToggleInput id={`rating-excluded-${rating.id}`} label={`Exclude ${rating.ratingText}`} hideLabel checked={rating.isExcluded} disabled={isLocked} title={isLocked ? LOCKED_TOOLTIP : undefined} onChange={(excluded) => onToggleExcluded(rating, excluded)} /> },
      { key: "irix", header: "IRIX Source", align: "center", render: (rating) => <CheckboxInput id={`irix-source-${rating.id}`} label={`IRIX Source: ${rating.ratingText}`} hideLabel checked={rating.irixSource} disabled={isLocked} onChange={(irixSource) => onIrixSourceChange(rating, irixSource)} /> },
    ];
    if (showChangeReason) base.push({ key: "changeReason", header: "Change Reason", render: (rating) => (rating.changeReason ? <TagPill label={rating.changeReason} tone="warning" /> : <span className="text-gray-400">–</span>) });
    base.push({ key: "source", header: "Source", render: (rating) => <SourcePill source={rating.source} /> });
    return base;
  }, [isLocked, showChangeReason, onDebitsChange, onUwDollarsChange, onToggleExcluded, onIrixSourceChange, onNotesChange]);

  return (
    <DataGrid
      caption="Assigned ratings"
      columns={columns}
      rows={ratings}
      rowKey={(rating) => rating.id}
      defaultSort={DEFAULT_SORT}
      sortRows={sortByColumnThenConditionAndRating}
      density="compact"
      isRowExcluded={(rating) => rating.isExcluded}
      actionsHeader="Rating actions"
      renderActions={(rating) => (
        <span className="inline-flex">
          <IconButton icon={Trash2} label={`Remove ${rating.ratingText}`} tone="danger" disabled={isLocked || isSyntheticSource(rating.source)} title={isLocked ? LOCKED_TOOLTIP : isSyntheticSource(rating.source) ? "Engine rows are excluded, not removed" : undefined} onClick={() => onRemove(rating)} size="sm" />
        </span>
      )}
      emptyState={
        <EmptyState
          icon={ListPlus}
          message="No conditions assigned yet. Add a condition to start rating this member."
          action={
            !isLocked ? (
              <Link to={addConditionTo} className="inline-flex min-h-[44px] items-center rounded-lg bg-brand-green px-4 py-2 text-sm font-medium text-white hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-brand-green-50">
                Add Condition
              </Link>
            ) : undefined
          }
        />
      }
    />
  );
}
